import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogOverlay, DialogPortal } from 'components/ui/dialog';
import { cn } from 'lib/utils';

export interface ExamDialogProps {
    open: boolean;
    /** Escape and a click on the scrim call this, like the shared Modal. */
    onClose: () => void;
    /** Max width from the board (e.g. `max-w-[480px]`). */
    widthClass: string;
    /** Set when the content renders a `DialogDescription`. */
    withDescription?: boolean;
    /** Where focus lands on open; defaults to the first control. */
    initialFocusRef?: React.RefObject<HTMLElement | null>;
    children: React.ReactNode;
}

/**
 * Exam dialog chrome: white 12px panel with no close button or header rule,
 * on the shared ink scrim. Content supplies its own `DialogTitle`.
 */
export function ExamDialog({
    open,
    onClose,
    widthClass,
    withDescription = true,
    initialFocusRef,
    children,
}: ExamDialogProps) {
    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onClose();
            }}
        >
            <DialogPortal>
                <DialogOverlay />
                {/* Click-through centring layer sized to the dynamic viewport (mobile Safari toolbars). */}
                <div className="pointer-events-none fixed inset-0 z-50 flex h-[100dvh] items-center justify-center p-4">
                    <DialogPrimitive.Content
                        data-surface="exam"
                        {...(withDescription ? {} : { 'aria-describedby': undefined })}
                        onOpenAutoFocus={(e) => {
                            if (!initialFocusRef?.current) return;
                            e.preventDefault();
                            initialFocusRef.current.focus();
                        }}
                        className={cn(
                            'pointer-events-auto max-h-[calc(100dvh-2rem)] w-full overflow-y-auto rounded-lg bg-card p-6 text-foreground shadow-[0_24px_60px_rgba(17,24,39,0.25)] outline-none',
                            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                            widthClass,
                        )}
                    >
                        {children}
                    </DialogPrimitive.Content>
                </div>
            </DialogPortal>
        </Dialog>
    );
}

export default ExamDialog;
